// External dependencies
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

// Internal dependencies
import { searchOffers } from '@/modules/offers/client/api/offers.api';
import NoContent from '@/modules/core/client/components/NoContent';
import SearchResultCard from './SearchResultCard';
import {
  SORT_DISTANCE,
  SORT_PRICE,
  SORT_RECOMMENDED,
  sortResults,
} from '../utils/results';

const EMPTY_RESULTS = { offers: [], nights: 0 };

/**
 * Is a bounding box complete enough to search with?
 */
function hasValidBounds(bounds) {
  return Boolean(
    bounds &&
      ['northEastLat', 'northEastLng', 'southWestLat', 'southWestLng'].every(
        key => Number.isFinite(bounds[key]),
      ),
  );
}

/**
 * Hosts inside the map area, listed with their prices.
 *
 * The map reports its bounds (see `SearchMap`), the sidebar passes them here
 * together with the current filters, and this list fetches matching hosts.
 */
export default function SearchResults({
  bounds,
  center,
  filters,
  placeName,
  selectedOfferId,
}) {
  const { t } = useTranslation('search');
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [sort, setSort] = useState(SORT_RECOMMENDED);
  // Only the latest request may update the list
  const latestRequest = useRef(0);

  const isSearchable = hasValidBounds(bounds);
  const boundsKey = isSearchable
    ? [
        bounds.northEastLat,
        bounds.northEastLng,
        bounds.southWestLat,
        bounds.southWestLng,
      ].join(',')
    : '';

  // Fetch hosts whenever the map area or the filters change
  useEffect(() => {
    const requestId = ++latestRequest.current;

    if (!isSearchable) {
      setResults(EMPTY_RESULTS);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    searchOffers({
      filters,
      northEastLat: bounds.northEastLat,
      northEastLng: bounds.northEastLng,
      southWestLat: bounds.southWestLat,
      southWestLng: bounds.southWestLng,
    })
      .then(data => {
        if (requestId !== latestRequest.current) {
          return;
        }
        setResults({
          offers: data?.offers ?? [],
          nights: data?.nights ?? 0,
        });
        setIsLoading(false);
      })
      .catch(() => {
        if (requestId !== latestRequest.current) {
          return;
        }
        setHasError(true);
        setIsLoading(false);
      });
  }, [boundsKey, filters]);

  // Bring the host picked on the map into view
  useEffect(() => {
    if (!selectedOfferId) {
      return;
    }
    const card = document.getElementById(`search-result-${selectedOfferId}`);
    if (card?.scrollIntoView) {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedOfferId, results]);

  const canSortByDistance = Boolean(center?.lat && center?.lng);
  const activeSort =
    sort === SORT_DISTANCE && !canSortByDistance ? SORT_RECOMMENDED : sort;
  const offers = sortResults(results.offers, activeSort, center);
  const count = offers.length;

  if (!isSearchable) {
    return (
      <NoContent
        icon="users"
        message={t('Move the map closer to a place to see hosts there.')}
      />
    );
  }

  return (
    <div className="search-results" aria-live="polite">
      <header className="search-results-header">
        <h4 className="search-results-count">
          {placeName
            ? t('{{count}} hosts in {{place}}', { count, place: placeName })
            : t('{{count}} hosts in this area', { count })}
          {isLoading && (
            <small className="search-results-loading text-muted">
              {' '}
              {t('Updating…')}
            </small>
          )}
        </h4>
        {count > 1 && (
          <label className="search-results-sort">
            <span className="text-muted">{t('Sort by')}</span>{' '}
            <select
              className="form-control input-sm"
              value={activeSort}
              onChange={({ target: { value } }) => setSort(value)}
            >
              <option value={SORT_RECOMMENDED}>{t('Recommended')}</option>
              <option value={SORT_PRICE}>{t('Price: low to high')}</option>
              {canSortByDistance && (
                <option value={SORT_DISTANCE}>{t('Distance')}</option>
              )}
            </select>
          </label>
        )}
      </header>

      {hasError && (
        <div className="alert alert-warning" role="alert">
          {t('Could not load hosts. Please try again.')}
        </div>
      )}

      {!hasError && !isLoading && count === 0 && (
        <NoContent
          icon="users"
          message={t(
            'No hosts found here. Try other dates, fewer guests, or zoom out.',
          )}
        />
      )}

      {offers.map(offer => (
        <SearchResultCard
          key={offer._id}
          offer={offer}
          center={center}
          nights={results.nights}
          isSelected={offer._id === selectedOfferId}
        />
      ))}
    </div>
  );
}

SearchResults.propTypes = {
  bounds: PropTypes.shape({
    northEastLat: PropTypes.number,
    northEastLng: PropTypes.number,
    southWestLat: PropTypes.number,
    southWestLng: PropTypes.number,
  }),
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  filters: PropTypes.string,
  placeName: PropTypes.string,
  selectedOfferId: PropTypes.string,
};
